import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiFileText,
  FiClock,
  FiCheck,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiRefreshCw,
  FiLogOut
} from "react-icons/fi";
import { twMerge } from "tailwind-merge";
import { consumirApi } from "../api/ph";
import DetallePreregistroRentas from "./DetallePreregistroRentas";
import Alerta from "./Alerta";

export default function HistorialRentas() {
  const navigate = useNavigate();
  const { nombreTramite } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL;

  const resolveUserData = () => {
    let storedUser = {};
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    } catch {}

    let decoded = {};
    const token = localStorage.getItem("token");
    if (token) {
      try {
        decoded = jwtDecode(token);
      } catch (e) {
        console.error("Error al decodificar token:", e);
      }
    }

    const rawCode = storedUser?.username || decoded?.preferred_username || decoded?.username || decoded?.sub || "";
    const cleanCode = (typeof rawCode === "string" && rawCode.startsWith("deudo-"))
      ? (rawCode.split("-")[1] || rawCode)
      : String(rawCode || "");

    return {
      userObj: { ...decoded, ...storedUser },
      numeroAfiliado: cleanCode,
      activeUserId: cleanCode || rawCode
    };
  };

  const initialUser = resolveUserData();
  const [user, setUser] = useState(initialUser.userObj);
  const [numeroAfiliado, setNumeroAfiliado] = useState(initialUser.numeroAfiliado);
  const [userId, setUserId] = useState(initialUser.activeUserId);

  const [cargando, setCargando] = useState(true);
  const [registros, setRegistros] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [limitePorPagina, setLimitePorPagina] = useState(5);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);

  // Estados para el detalle
  const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
  const [detalleDocumento, setDetalleDocumento] = useState(null);
  const [detalleDocumento_sub, setDetalleDocumento_sub] = useState([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [alerta, setAlerta] = useState(null);

  const detalleRef = useRef(null);

  // Cargar datos de historial de rentas con paginación
  const cargarHistorial = useCallback(async (pagina = 1, limite = limitePorPagina, targetUserId = userId) => {
    const idToUse = targetUserId || resolveUserData().activeUserId;
    if (!idToUse) {
      console.warn("No se encontró userId para consultar historial.");
      setCargando(false);
      return;
    }
    setCargando(true);

    try {
      const endpoint = `${apiUrl}/preregistros/historial-rentas/${idToUse}?page=${pagina}&limit=${limite}`;
      console.log(`[HistorialRentas] Consultando ${endpoint}...`);
      const { success, data: responseData, message } = await consumirApi({
        url: endpoint,
        method: "GET"
      });
      console.log("[HistorialRentas] Respuesta recibida:", success, responseData);

      if (success && responseData) {
        let items = [];
        let total = 0;
        let pages = 1;

        if (Array.isArray(responseData.data)) {
          items = responseData.data;
          total = responseData.total !== undefined ? responseData.total : items.length;
          pages = responseData.total_pages !== undefined ? responseData.total_pages : Math.ceil(total / limite);
        } else if (Array.isArray(responseData)) {
          items = responseData;
          total = items.length;
          pages = Math.ceil(total / limite);
        }

        setRegistros(items);
        setTotalRegistros(total);
        setTotalPaginas(pages > 0 ? pages : 1);
        setPaginaActual(pagina);
      } else {
        console.warn("No se obtuvo información de historial:", message);
        setRegistros([]);
        setTotalRegistros(0);
        setTotalPaginas(1);
      }
    } catch (error) {
      console.error("Error al cargar historial de rentas:", error);
      setAlerta({
        tipo: "error",
        titulo: "Error al cargar",
        mensaje: "Ocurrió un error al obtener el historial de trámites."
      });
      setRegistros([]);
    } finally {
      setCargando(false);
    }
  }, [apiUrl, userId, limitePorPagina]);

  useEffect(() => {
    const info = resolveUserData();
    if (info.userObj && (!user || !userId)) {
      setUser(info.userObj);
      setNumeroAfiliado(info.numeroAfiliado);
      setUserId(info.activeUserId);
    }
    cargarHistorial(paginaActual, limitePorPagina, info.activeUserId);
  }, [cargarHistorial, paginaActual, limitePorPagina]);

  // Abrir detalle del trámite
  const handleVerDetalle = useCallback(async (fila) => {
    setCargandoDetalle(true);
    const { id_preregistro } = fila;

    try {
      const { success, data, message } = await consumirApi({
        url: `${apiUrl}/preregistros/${id_preregistro}/documentos`,
        method: "GET"
      });

      if (success && data?.data && data.data.length > 0) {
        const documentos = data.data;
        setDetalleDocumento(documentos[0] || {});
        setDetalleDocumento_sub(documentos);
        setRegistroSeleccionado(fila);
      } else {
        setAlerta({
          tipo: "warning",
          titulo: "Sin documentos",
          mensaje: `El folio #${fila.folio || id_preregistro} no cuenta con documentos asociados en el sistema.`
        });
        setRegistroSeleccionado(null);
      }
    } catch (err) {
      console.error("Error al obtener detalle del trámite:", err);
      setAlerta({
        tipo: "error",
        titulo: "Error de consulta",
        mensaje: "No fue posible consultar los documentos de este trámite."
      });
    } finally {
      setCargandoDetalle(false);
    }
  }, [apiUrl]);

  const handleCambiarLimite = (nuevoLimite) => {
    const num = Number(nuevoLimite);
    setLimitePorPagina(num);
    setPaginaActual(1);
    cargarHistorial(1, num);
  };

  const handleCerrarSesion = () => {
    localStorage.clear();
    const loginUrl = process.env.REACT_APP_KEYCLOAK_LOGIN_URL;
    if (loginUrl) window.location.replace(loginUrl);
    else navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      {/* Barra superior de navegación */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(`/tramite/${nombreTramite || "Rentas"}`)}
            className="flex items-center gap-2 px-3.5 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 hover:text-primary rounded-xl font-bold text-xs uppercase tracking-wider transition-all border border-slate-200 active:scale-95 shadow-2xs"
            title="Regresar a Rentas"
          >
            <FiArrowLeft className="size-4 text-primary" />
            <span>Volver a Rentas</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest">IPEJAL DIGITAL</span>
              <span className="text-xs font-black text-slate-800 tracking-tight">{user?.name || "Afiliado"}</span>
              <span className="text-[10px] text-slate-500 font-bold">
                {numeroAfiliado ? `Usuario: ${numeroAfiliado}` : ""}
                {user?.rfc ? ` | RFC: ${user.rfc}` : ""}
              </span>
            </div>
            <button
              onClick={handleCerrarSesion}
              className="size-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-xl transition-colors border border-slate-200 shadow-2xs"
              title="Cerrar Sesión"
            >
              <FiLogOut className="size-4.5" />
            </button>
          </div>
        </div>
      </header>

      {/* Contenedor principal */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
        <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col">
          {/* Header de la tarjeta */}
          <div className="p-6 sm:p-8 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="size-11 bg-primary/10 text-primary rounded-2xl flex items-center justify-center shadow-xs border border-primary/10">
                <FiClock className="size-6" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight">
                  Historial de Trámites — Rentas
                </h1>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                  Consulta de expedientes y solicitudes anteriores
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 self-end sm:self-auto">
              {/* Selector de límite por página */}
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-2xs">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Ver:</span>
                <select
                  value={limitePorPagina}
                  onChange={(e) => handleCambiarLimite(e.target.value)}
                  className="bg-transparent font-bold text-xs text-slate-700 outline-none cursor-pointer"
                >
                  <option value={5}>5 por pág.</option>
                  <option value={10}>10 por pág.</option>
                  <option value={20}>20 por pág.</option>
                </select>
              </div>

              {/* Botón de recarga */}
              <button
                onClick={() => cargarHistorial(paginaActual, limitePorPagina)}
                disabled={cargando}
                className="size-9 flex items-center justify-center bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-primary hover:border-primary/30 transition-all shadow-2xs active:scale-95 disabled:opacity-50"
                title="Recargar historial"
              >
                <FiRefreshCw className={twMerge("size-4", cargando && "animate-spin text-primary")} />
              </button>
            </div>
          </div>

          {/* Contenedor de la tabla */}
          <div className="overflow-x-auto min-h-[350px] relative">
            {cargando ? (
              <div className="py-24 flex flex-col items-center justify-center gap-4 text-center">
                <div className="size-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Cargando historial de trámites...
                </p>
              </div>
            ) : registros.length === 0 ? (
              <div className="py-24 flex flex-col items-center justify-center gap-3 text-center px-4">
                <div className="size-16 bg-slate-100 rounded-3xl flex items-center justify-center text-slate-300">
                  <FiClock className="size-8" />
                </div>
                <h3 className="text-base font-black text-slate-700 uppercase tracking-tight">Sin registros en el historial</h3>
                <p className="text-xs font-bold text-slate-400 max-w-sm">
                  Actualmente no cuentas con solicitudes previas registradas en el historial de Rentas.
                </p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-400 bg-slate-50/70">
                    <th className="px-6 py-4">Folio</th>
                    <th className="px-6 py-4 text-center">Estatus Documentos</th>
                    <th className="px-6 py-4 text-center">Fecha Preregistro</th>
                    <th className="px-6 py-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {registros.map((item, idx) => {
                    const estatus = item.nombre_estado || item.estado || "";
                    const estatusUpper = estatus.toUpperCase().trim();
                    const folioVal = item.folio || item.id_preregistro || "-";

                    return (
                      <tr
                        key={item.id_preregistro || idx}
                        className="hover:bg-slate-50/60 transition-colors"
                      >
                        <td className="px-6 py-4 font-black text-slate-800 text-sm">
                          {folioVal}
                        </td>

                        <td className="px-6 py-4 text-center">
                          {estatusUpper === "APROBADO" ? (
                            <div className="flex justify-center">
                              <div className="size-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-xs" title="Aprobado">
                                <FiCheck className="size-3.5 stroke-[3]" />
                              </div>
                            </div>
                          ) : estatusUpper === "CANCELADO" ? (
                            <div className="flex justify-center">
                              <div className="size-6 rounded-full bg-slate-400 text-white flex items-center justify-center shadow-xs" title="Cancelado">
                                <svg className="size-3 stroke-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                </svg>
                              </div>
                            </div>
                          ) : (
                            <span
                              className={twMerge(
                                "inline-flex items-center px-3 py-1 rounded-lg text-[11px] font-bold uppercase tracking-widest border",
                                estatusUpper === "APROBADO"
                                  ? "bg-white text-green-700 border-green-300"
                                  : (estatusUpper === "RECHAZADO" || estatusUpper === "OBSERVADO" || estatusUpper.includes("RECHAZ"))
                                  ? "bg-white text-red-600 border-red-300"
                                  : estatusUpper === "PENDIENTE"
                                  ? "bg-white text-amber-700 border-amber-300"
                                  : "bg-white text-slate-600 border-slate-300"
                              )}
                            >
                              {estatus}
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center text-slate-500 font-bold whitespace-nowrap">
                          {(() => {
                            const f = item.fecha_preregistro || item.created_at || item.fecha;
                            if (!f) return "-";
                            try {
                              return new Date(f).toLocaleDateString("es-MX", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true
                              });
                            } catch {
                              return f;
                            }
                          })()}
                        </td>

                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleVerDetalle(item)}
                            disabled={cargandoDetalle}
                            className="inline-flex items-center justify-center gap-2 px-4 h-9 rounded-xl text-xs font-bold transition-all duration-200 active:scale-95 shadow-xs bg-white hover:bg-slate-50 text-slate-700 hover:text-primary border border-slate-300 hover:border-primary/40 whitespace-nowrap disabled:opacity-50"
                          >
                            <FiFileText className="size-4 text-slate-400" />
                            <span>Ver Detalle</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer de Paginación */}
          {totalRegistros > 0 && (
            <div className="p-4 sm:px-8 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center sm:text-left">
                Mostrando{" "}
                <span className="text-slate-700 font-black">
                  {(paginaActual - 1) * limitePorPagina + 1}
                </span>{" "}
                a{" "}
                <span className="text-slate-700 font-black">
                  {Math.min(paginaActual * limitePorPagina, totalRegistros)}
                </span>{" "}
                de <span className="text-slate-700 font-black">{totalRegistros}</span> trámites
              </div>

              <div className="flex items-center gap-1.5">
                {/* Primera página */}
                <button
                  onClick={() => setPaginaActual(1)}
                  disabled={paginaActual <= 1 || cargando}
                  className="size-8.5 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/30 transition-all disabled:opacity-40 disabled:pointer-events-none shadow-2xs"
                  title="Primera página"
                >
                  <FiChevronsLeft className="size-4" />
                </button>

                {/* Página anterior */}
                <button
                  onClick={() => setPaginaActual((p) => Math.max(p - 1, 1))}
                  disabled={paginaActual <= 1 || cargando}
                  className="size-8.5 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/30 transition-all disabled:opacity-40 disabled:pointer-events-none shadow-2xs"
                  title="Página anterior"
                >
                  <FiChevronLeft className="size-4" />
                </button>

                {/* Indicador de página */}
                <span className="px-3.5 py-1.5 bg-primary/10 text-primary rounded-xl font-black text-xs">
                  {paginaActual} / {totalPaginas}
                </span>

                {/* Página siguiente */}
                <button
                  onClick={() => setPaginaActual((p) => Math.min(p + 1, totalPaginas))}
                  disabled={paginaActual >= totalPaginas || cargando}
                  className="size-8.5 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/30 transition-all disabled:opacity-40 disabled:pointer-events-none shadow-2xs"
                  title="Página siguiente"
                >
                  <FiChevronRight className="size-4" />
                </button>

                {/* Última página */}
                <button
                  onClick={() => setPaginaActual(totalPaginas)}
                  disabled={paginaActual >= totalPaginas || cargando}
                  className="size-8.5 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/30 transition-all disabled:opacity-40 disabled:pointer-events-none shadow-2xs"
                  title="Última página"
                >
                  <FiChevronsRight className="size-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Panel lateral / Drawer de Detalle */}
      <AnimatePresence>
        {registroSeleccionado && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setRegistroSeleccionado(null)}
              className="fixed inset-0 bg-black/35 backdrop-blur-xs z-[140]"
            />

            <motion.div
              ref={detalleRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 h-screen w-full md:w-1/2 lg:w-1/2 bg-slate-50 border-l border-slate-200/80 shadow-2xl z-[150] overflow-hidden flex flex-col rounded-l-[2rem] sm:rounded-l-[2.5rem]"
            >
              <header className="flex justify-between items-center p-6 sm:p-8 border-b border-slate-200/60 relative z-10 bg-white">
                <div className="space-y-1">
                  <h2 className="text-lg sm:text-xl font-black text-slate-800 tracking-tight uppercase">
                    DETALLE DE REGISTRO #{registroSeleccionado.folio || registroSeleccionado.id_preregistro}
                  </h2>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Información detallada y bitácora de documentos
                  </p>
                </div>
                <button
                  className="size-10 flex items-center justify-center bg-slate-50 hover:bg-red-50 text-slate-500 hover:text-red-600 rounded-xl border border-slate-200 hover:border-red-200 transition-all active:scale-95 shadow-xs"
                  onClick={() => setRegistroSeleccionado(null)}
                >
                  <FiX className="text-xl" />
                </button>
              </header>

              <div className="relative z-10 flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
                <DetallePreregistroRentas
                  data={registroSeleccionado}
                  bitacora={detalleDocumento}
                  bitacora_doc={detalleDocumento_sub}
                  estatusRentas={registroSeleccionado?.tramite_estatus || null}
                  folioFilaVirtual={registroSeleccionado?.folio_fila_virtual || null}
                  reloadDetail={() => handleVerDetalle(registroSeleccionado)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Alerta flotante */}
      <AnimatePresence>
        {alerta && (
          <Alerta
            tipo={alerta.tipo}
            titulo={alerta.titulo}
            mensaje={alerta.mensaje}
            onClose={() => setAlerta(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
